import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import Pagination from '../../components/Pagination.js';
import TruncatedCell from '../../components/TruncatedCell.js';
import UserSelect from '../../components/UserSelect.js';
import usePaginatedFetch from '../../utils/usePaginatedFetch.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';

function OpportunityListPage() {
    // 1. State（状態）の定義
    const [inputName, setInputName] = useState('');
    const [inputOwner, setInputOwner] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchOwner, setSearchOwner] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newApplicantName, setNewApplicantName] = useState('');
    const [newOwnerName, setNewOwnerName] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    const [searchStatus, setSearchStatus] = useState('ongoing'); // 'all', 'ongoing', 'closed'
    const [onlyMine, setOnlyMine] = useState(false);

    // 💡 ページ遷移用のnavigate関数を取得
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // 2. 検索パラメータをuseMemoで安定化し、usePaginatedFetchに渡す
    const searchParams = useMemo(() => {
        const params = {};
        if (searchName) params.applicant_name = searchName;
        if (searchOwner) params.owner_id = searchOwner;
        if (searchStatus === 'ongoing') params.is_closed = 'false';
        if (searchStatus === 'closed') params.is_closed = 'true';
        if (onlyMine && currentUser) params.owner_id = currentUser.id;
        return params;
    }, [searchName, searchOwner, searchStatus, onlyMine, currentUser]);

    const opps = usePaginatedFetch('/opportunities/search/', searchParams, 20);


    useEffect(() => {
        // 「自分の担当のみ」が有効な場合、currentUser の取得を待ってからフェッチする
        if (onlyMine && !currentUser) return;
        opps.fetch();
    }, [searchParams, opps.page, onlyMine, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // 5. イベントハンドラ
    const handleSearchClick = () => {
        opps.setPage(1); // 検索時はページをリセット
        setSearchName(inputName);
        setSearchOwner(inputOwner);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setNewApplicantName('');
        // デフォルトでログインユーザーを選択
        setNewOwnerName(currentUser ? currentUser.id : '');
        setModalError(null);
        setModalLoading(false);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleCreateOpportunity = async (event) => {
        event.preventDefault();
        if (!newApplicantName || !newOwnerName) {
            setModalError('求職者名、担当者は必須です。');
            return;
        }
        setModalLoading(true);
        setModalError(null);
        try {
            const response = await api.fetch('/opportunities/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    applicant_name: newApplicantName,
                    owner_id: newOwnerName, // IDを文字列として送る
                }),
            });
            if (!response) return; // Redirected
            if (!response.ok) {
                const errorMessage = await handleApiError(response, '作成に失敗しました。');
                throw new Error(errorMessage);
            }
            const newOpp = await response.json();
            setModalLoading(false);
            handleCloseModal();
            opps.fetch();
            navigate(`/opportunity/${newOpp.id}`);
        } catch (err) {
            setModalLoading(false);
            setModalError(err.message);
        }
    };

    // 💡 行クリック時のハンドラ
    const handleRowClick = (opportunityId) => {
        navigate(`/opportunity/${opportunityId}`);
    };

    // 6. レンダリング (JSX)
    return (
        <React.Fragment>
            <h1 className="page-title">案件一覧</h1>

            {/* ツールバーセクション */}
            <div className="toolbar-section">
                <div className="search-area" style={{ flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            type="text"
                            placeholder="求職者名..."
                            className="search-input"
                            value={inputName}
                            onChange={(e) => setInputName(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearchClick();
                            }}
                        />
                        <div style={{ width: '18rem' }}>
                            <UserSelect
                                id="filterOwner"
                                value={inputOwner}
                                onChange={setInputOwner}
                                placeholder="担当者を選択..."
                            />
                        </div>
                        <button className="search-button" onClick={handleSearchClick}>
                            検索
                        </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {/* 状況フィルター (セグメントコントロール風) */}
                        <div style={{
                            display: 'inline-flex',
                            backgroundColor: '#f1f3f4',
                            borderRadius: '8px',
                            padding: '3px',
                            border: '1px solid #e0e0e0'
                        }}>
                            {['ongoing', 'closed', 'all'].map((status) => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => setSearchStatus(status)}
                                    style={{
                                        padding: '5px 15px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        backgroundColor: searchStatus === status ? '#fff' : 'transparent',
                                        color: searchStatus === status ? '#0176d3' : '#5f6368',
                                        fontWeight: searchStatus === status ? '700' : '500',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        boxShadow: searchStatus === status ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {status === 'ongoing' ? '進行中' : status === 'closed' ? '終了' : 'すべて'}
                                </button>
                            ))}
                        </div>

                        {/* 自分の担当のみチェックボックス */}
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#4a5c7c',
                            userSelect: 'none'
                        }}>
                            <input
                                type="checkbox"
                                checked={onlyMine}
                                onChange={(e) => setOnlyMine(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            自分の担当のみ
                        </label>
                    </div>
                </div>
                <div className="create-area">
                    <button className="modal-button-confirm" onClick={handleOpenModal}>
                        + 新規案件
                    </button>
                </div>
            </div>

            {/* 案件一覧表示 */}
            <div className="meeting-list-card">
                <h2 className="card-title">案件リスト</h2>
                {opps.loading && opps.items.length === 0 && <SkeletonLoader variant="list" rows={5} />}
                {opps.error && <p className="error-message">エラー: {opps.error}</p>}

                {/* 読み込み中かつ既にデータがある場合は、不透明度を下げて表示を継続（チカチカを防止） */}
                {(!opps.loading || opps.items.length > 0) && !opps.error && (
                    <div className="table-responsive" style={{
                        opacity: opps.loading ? 0.6 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: opps.loading ? 'none' : 'auto' // 読み込み中は操作を無効化
                    }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>求職者名</th>
                                    <th>担当者</th>
                                    <th>状況</th>
                                </tr>
                            </thead>
                            <tbody>
                                {opps.items.length > 0 ? (
                                    opps.items.map(opp => (
                                        <tr
                                            key={opp.id}
                                            onClick={() => handleRowClick(opp.id)}
                                            className="clickable-row"
                                        >
                                            <td>
                                                <TruncatedCell text={opp.applicant_name || '-'} />
                                            </td>
                                            <td>{opp.owner_name}</td>
                                            <td>
                                                {(() => {
                                                    const label = opp.stage_name || (opp.is_closed ? '終了' : '進行中');
                                                    return (
                                                        <div className={`status-badge compact ${opp.is_closed ? 'closed' : 'ongoing'}`} title={label}>
                                                            <span className="status-badge-dot"></span>
                                                            <span className="status-badge-label">{label}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="no-data">該当する案件はありません。</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ページネーション */}
                {!opps.loading && !opps.error && opps.totalPages > 0 && (
                    <Pagination
                        currentPage={opps.page}
                        totalPages={opps.totalPages}
                        totalItems={opps.total}
                        perPage={opps.perPage}
                        onPageChange={opps.setPage}
                    />
                )}
            </div>

            {/* 案件作成モーダル */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <form onSubmit={handleCreateOpportunity}>
                            <h3 className="modal-title">新規案件作成</h3>
                            <div className="form-group">
                                <label htmlFor="oppApplicantName">求職者名 *</label>
                                <input
                                    id="oppApplicantName" type="text" value={newApplicantName}
                                    onChange={(e) => setNewApplicantName(e.target.value)}
                                    disabled={modalLoading}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="ownerName">担当者 *</label>
                                <UserSelect
                                    id="ownerId"
                                    value={newOwnerName}
                                    onChange={(val) => setNewOwnerName(val)}
                                    placeholder="担当者を選択..."
                                    disabled={modalLoading}
                                />
                            </div>
                            {modalError && (
                                <p className="modal-error-message">{modalError}</p>
                            )}
                            <div className="modal-actions">
                                <button
                                    type="button" className="modal-button-cancel"
                                    onClick={handleCloseModal} disabled={modalLoading}
                                >
                                    キャンセル
                                </button>
                                <button
                                    type="submit" className="modal-button-confirm"
                                    disabled={modalLoading}
                                >
                                    {modalLoading ? '作成中...' : '作成'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

export default OpportunityListPage;
